import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlayCircle, GraduationCap } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const ytId = (u) => {
  try {
    const url = new URL(u);
    if (url.hostname.includes("youtu.be")) return url.pathname.slice(1);
    if (url.searchParams.get("v")) return url.searchParams.get("v");
    const m = url.pathname.match(/\/(embed|shorts)\/([^/?]+)/);
    return m ? m[2] : "";
  } catch { return ""; }
};

// Public video tutorial library, grouped by section. Used on Login, Pricing and in-app.
export default function TutorialLibrary({ triggerClassName = "", triggerLabel }) {
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/support/tutorials").then((r) => setItems(r.data || [])).catch(() => {});
  }, [open]);

  const sections = items.reduce((acc, it) => { (acc[it.section] = acc[it.section] || []).push(it); return acc; }, {});

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="tutorials-open-btn" className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${triggerClassName}`}>
        <GraduationCap size={18} weight="bold" /> {triggerLabel || tr("Guida & Tutorial", "Guides & Tutorials")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tutorials-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-sm flex items-center gap-2"><GraduationCap size={18} weight="fill" /> {tr("Guida & Tutorial", "Guides & Tutorials")}</DialogTitle>
            <DialogDescription className="text-xs">{tr("Video guida per ogni area dell'hub.", "Video guides for each area of the hub.")}</DialogDescription>
          </DialogHeader>
          {items.length === 0 ? (
            <p className="py-10 text-center text-muted-foreground text-sm" data-testid="tutorials-empty">{tr("Nessun video disponibile al momento.", "No videos available yet.")}</p>
          ) : (
            <div className="space-y-5">
              {Object.entries(sections).map(([sec, list]) => (
                <div key={sec} data-testid={`tutorials-section-${sec}`}>
                  <p className="font-display font-bold uppercase tracking-wide text-xs text-primary mb-2">{sec}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {list.map((it) => {
                      const id = ytId(it.youtube_url);
                      return (
                        <a key={it.id} href={it.youtube_url} target="_blank" rel="noreferrer" data-testid={`tutorial-${it.id}`} className="group rounded-sm border border-border overflow-hidden hover:border-primary transition-colors">
                          <div className="relative aspect-video bg-muted">
                            {id && <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors"><PlayCircle size={40} weight="fill" className="text-white/90" /></div>
                          </div>
                          <p className="px-2 py-2 text-sm font-medium">{it.title}</p>
                        </a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
